/** @format */

import {
  forwardRef,
  HTMLAttributes,
  MutableRefObject,
  ReactNode,
  useCallback,
  useLayoutEffect,
  useState,
} from 'react';

import {OnChangeFn, Row, RowSelectionState} from '@tanstack/react-table';

import {TableVirtuoso} from 'react-virtuoso';

import {Table} from '@mui/material';

import {
  ColumnType,
  ConstantsList,
  SortDirection,
  TanStackTableData,
  TableVariant,
} from './core/types';
import {DEFAULT_TABLE_HEIGHT, HEAD_HEIGHT} from './core/constants';
import {TableBodyRow} from './core/components/TableBodyRow';
import {TableHeadRow} from './core/components/TableHeadRow';
import {StyledTableBody} from './core/styles';
import {TableShell} from './core/TableShell';
import {getClampedHeight} from './core/helpers/getClampedHeight';
import {useTableModel} from './core/useTableModel';

interface Props {
  columns: ColumnType[];
  data: TanStackTableData[];
  tableHeight?: number; // высота не превысит высоты вкладки браузера
  search?: string;
  searchByColumnIds?: string[];
  defaultSortBy?: string;
  defaultOrderBy?: SortDirection;
  enableSorting?: boolean;
  enableFilters?: boolean;
  filterOrder?: string[];
  onOpenSubRows?: (value: boolean) => void;
  onFilterChange?: (columnId: string, value: string | null | undefined) => void;
  rowSelection?: RowSelectionState;
  setRowSelection?: OnChangeFn<RowSelectionState>;
  variant?: TableVariant;
  constants?: ConstantsList;
  filterBarRef?: React.RefObject<HTMLDivElement>;
  rowIdKey?: string;
  isRowSelectionDisabled?: (row: Row<TanStackTableData>) => boolean;
  exportDataRef?: MutableRefObject<TanStackTableData[]>;
  onExportDataChange?: (dataLength: number) => void;
  filterBarActions?: ReactNode;
  // Опциональный рендерер закреплённой строки в подвале таблицы (`<tfoot>`).
  // Имя совпадает с пропом `react-virtuoso.TableVirtuoso.fixedFooterContent`,
  // прокидывается как-есть. Используется, например, для тотал-строки.
  fixedFooterContent?: () => ReactNode;
}

const TableVirtualized = ({
  columns,
  data,
  tableHeight = DEFAULT_TABLE_HEIGHT,
  searchByColumnIds,
  search,
  defaultOrderBy,
  defaultSortBy,
  onOpenSubRows,
  onFilterChange,
  rowSelection,
  setRowSelection,
  enableSorting = true,
  enableFilters = false,
  filterOrder = [],
  variant = 'dark',
  constants = {},
  filterBarRef,
  rowIdKey = 'id',
  isRowSelectionDisabled,
  exportDataRef,
  onExportDataChange,
  filterBarActions,
  fixedFooterContent,
}: Props) => {
  const {
    table,
    rows,
    headerGroups,
    allSubColumnIds,
    isWithSubRows,
    enableRowSelection,
    isAllSelectionDisabled,
    filters,
    filterOptions,
    handleFilterChange,
    handleResetFilters,
  } = useTableModel({
    columns,
    data,
    search,
    searchByColumnIds,
    defaultSortBy,
    defaultOrderBy,
    enableSorting,
    enableFilters,
    filterOrder,
    onOpenSubRows,
    onFilterChange,
    rowSelection,
    setRowSelection,
    rowIdKey,
    isRowSelectionDisabled,
    exportDataRef,
    onExportDataChange,
  });

  const [calculatedTableHeight, setCalculatedTableHeight] = useState<string>(`${tableHeight}px`);

  const applyHeight = useCallback(() => {
    const newHeight = getClampedHeight(
      constants.headHeight ?? HEAD_HEIGHT,
      tableHeight,
      rows.length,
    );
    setCalculatedTableHeight(`${newHeight}px`);
  }, [constants, tableHeight, rows]);

  useLayoutEffect(() => {
    applyHeight();

    const handleResize = () => {
      applyHeight();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [applyHeight]);

  // Решение для одновременного прокидывания props и ref в TableVirtuoso
  const makeTableBody = useCallback(
    () =>
      // eslint-disable-next-line react/display-name
      forwardRef<HTMLTableSectionElement, HTMLAttributes<HTMLTableSectionElement>>((props, ref) => (
        <StyledTableBody ref={ref} variant={variant} {...props} />
      )),
    [variant],
  );

  return (
    <TableShell
      variant={variant}
      enableFilters={enableFilters}
      filters={filters}
      filterOptions={filterOptions}
      onFilterChange={handleFilterChange}
      onResetFilters={handleResetFilters}
      columns={columns}
      filterBarRef={filterBarRef}
      filterBarActions={filterBarActions}
    >
      <TableVirtuoso
        data-testid='table-virtuoso'
        totalCount={rows.length}
        style={{height: calculatedTableHeight}}
        components={{
          Table: props => <Table sx={{tableLayout: 'fixed'}} {...props} />,
          TableRow: props => (
            <TableBodyRow
              isWithSubRows={isWithSubRows}
              rows={rows}
              enableRowSelection={enableRowSelection}
              isRowSelectionDisabled={isRowSelectionDisabled}
              constants={constants}
              variant={variant}
              {...props}
            />
          ),
          TableBody: makeTableBody(),
        }}
        fixedHeaderContent={() => {
          return headerGroups.map((headerGroup, index) => (
            <TableHeadRow
              key={headerGroup.id}
              isWithSubRows={isWithSubRows}
              headerGroup={headerGroup}
              allSubColumnIds={allSubColumnIds}
              table={table}
              index={index}
              enableRowSelection={enableRowSelection}
              isAllSelectionDisabled={isAllSelectionDisabled}
              constants={constants}
              variant={variant}
            />
          ));
        }}
        fixedFooterContent={fixedFooterContent}
      />
    </TableShell>
  );
};

export {TableVirtualized};
